import { colors } from '@kmns-internal/furikake'
import ChevronRight from '@mui/icons-material/ChevronRight'
import ContentCopyOutlined from '@mui/icons-material/ContentCopyOutlined'
import DeleteOutline from '@mui/icons-material/DeleteOutline'
import ExpandMore from '@mui/icons-material/ExpandMore'
import {
  Box,
  Checkbox,
  FormControlLabel,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material'
import {
  type MultipleChoice,
  ResponseEmployeeSubTypeEnum,
  ResponseTypeEnum,
  type TemplateNodeSchema,
  TemplateNodeTypeEnum,
} from '@ulysses-inc/harami_api_client'
import { type ReactNode, useRef, useState } from 'react'
import { DropTargetMonitor, useDrag, useDrop } from 'react-dnd'
import { useDispatch, useSelector } from 'react-redux'
import { getRelatedExcelConversionFlowInSection } from 'src/features/templateEdit/common/getRelatedExcelConversionFlow'
import {
  DragItem,
  HoveringPosition,
  MoveNodeEvent,
} from 'src/features/templateEdit/dragAndDrop/DnDTemplateType'
import { DnDUtils } from 'src/features/templateEdit/dragAndDrop/DnDUtils'
import {
  copyNodeParams,
  copyPageParams,
} from 'src/features/templateEdit/section/CopySection'
import {
  addSectionToPage as addSectionToPageAction,
  addSectionToSection as addSectionToSectionAction,
  changeActiveNodeId as changeActiveNodeIdAction,
  changeSectionToNext as changeSectionToNextAction,
  deleteTemplateNode as deleteTemplateNodeAction,
  moveTemplateNode as moveTemplateNodeAction,
  updateTemplateNode as updateTemplateNodeAction,
} from 'src/state/ducks/templates/actions'
import { useTemplateNodeNodes } from 'src/state/ducks/templates/selectors'
import type { RootState } from 'src/state/store'
import { isDefined } from 'src/util/isDefined'
import DeleteConfirmDialog from 'src/views/components/dialogs/DeleteConfirmDialog'
import { createNode } from '../common/createNode'
import { ERROR_MESSAGES } from '../common/errorMessages'
import { useErrorDialog } from '../common/useErrorDialog'
import AddTemplateModifyButtonGroup from '../itemAppender/AddTemplateModifyButtonGroup'
import SelectNodeEditor from '../question/SelectNodeEditor'
import {
  BasicTableCell,
  CheckBoxText,
  ColumnText,
  ConfirmModalContentList,
  DraggingPlaceholder,
  DragHandle,
  HandleIcon,
  SectionContentWrapper,
  SectionNameParagraphWrapper,
  SectionOpenButtonWrapper,
  SectionTableCell,
  SectionWrapper,
  Table,
} from './Section.dumb'
import SectionContent from './SectionContent'
import {
  containsTableSection,
  isTableSectionNode,
  shouldBlockTableMoveIntoEmployeeCheck,
} from './table/model'

interface Props {
  node: TemplateNodeSchema
  isActive: boolean
  parentNode: TemplateNodeSchema | null
  path: number[]
}

const Section = (props: Props) => {
  const dispatch = useDispatch()
  const { showError, errorDialog: moveErrorDialog } = useErrorDialog()

  const {
    editTemplate: { isShowScore, isAudit, excelConversionFlows },
    templatePages: { activePageId, activeNodeId, templatePages, templateNodes },
  } = useSelector((state: RootState) => state.templatesState)

  const changeActiveNodeId = (nextActiveNodeId: number) =>
    dispatch(changeActiveNodeIdAction(nextActiveNodeId))

  const updateTemplateNode = (nodeId: number, changeNode: TemplateNodeSchema) =>
    dispatch(
      // 以下のケースで利用されている
      // - セクション名入力欄にセクション名を入力する
      // - セクションを繰り返すかどうかを設定する
      // - セクションを従業員チェックタイプに設定する
      updateTemplateNodeAction(nodeId, changeNode),
    )

  const changeSectionToNext = (nodeId: number, node: TemplateNodeSchema) => {
    dispatch(changeSectionToNextAction(nodeId, node))
  }

  const deleteTemplateNode = (
    nodeId: number,
    parentNode: TemplateNodeSchema | null,
  ) => {
    dispatch(deleteTemplateNodeAction(nodeId, parentNode))
  }

  const copySectionToNode = (
    originalNodeId: number,
    copiedNode: TemplateNodeSchema,
    childNodes: { [key: number]: TemplateNodeSchema },
    parentNodeId: number,
  ) => {
    dispatch(
      addSectionToSectionAction(
        originalNodeId,
        copiedNode,
        childNodes,
        parentNodeId,
      ),
    )
  }

  const copySectionToPage = (
    originalNodeId: number,
    copiedNode: TemplateNodeSchema,
    childNodes: { [key: number]: TemplateNodeSchema },
    parentPageId: number,
  ) => {
    dispatch(
      addSectionToPageAction(
        originalNodeId,
        copiedNode,
        childNodes,
        parentPageId,
      ),
    )
  }

  // 隣り合うセクション同士を並べ替えたときに発動するっぽい
  const moveNode = (event: MoveNodeEvent) => {
    const isSameParent =
      (!event.dragNodeParent && !event.dropNodeParent) ||
      (!!event.dragNodeParent &&
        !!event.dropNodeParent &&
        event.dragNodeParent.id === event.dropNodeParent.id)
    dispatch(moveTemplateNodeAction(event, isSameParent))
  }

  const sumSectionScore = (targetNode: TemplateNodeSchema): number => {
    // 子要素が存在しない場合に再帰を終了して値を返却
    if (targetNode.nodes.length === 0) return 0
    const childNodes = targetNode.nodes
      .map((id: number) => templateNodes[id])
      .filter(isDefined)
    const questionSumScore = childNodes
      .filter(thisNode => thisNode.type === TemplateNodeTypeEnum.Question)
      .map(thisNode => {
        const multipleChoices = thisNode.question?.responseMultipleChoices ?? []
        if (multipleChoices.length === 0) return 0
        const maxScore = (multipleChoices[0]?.responses ?? [])
          .filter((m: MultipleChoice) => !m.isExcludedFromScoring)
          .map(response => response.score)
          .reduce((prev, cur) => Math.max(prev, cur), 0)
        return maxScore
      })
      .reduce((prev, cur) => prev + cur, 0)
    return (
      questionSumScore +
      childNodes
        .map(thisNode => sumSectionScore(thisNode))
        .reduce((prev, cur) => prev + cur, 0)
    )
  }

  const addTemplateNode = (
    targetNode: TemplateNodeSchema | null,
    parentNode: TemplateNodeSchema | null,
    position: 'top' | 'bottom',
    addTemplateNodeType: TemplateNodeTypeEnum,
  ) => {
    createNode(templateNodes, dispatch, {
      targetNode,
      parentNode,
      position,
      addTemplateNodeType,
      question: targetNode?.question,
    })
  }

  const copySection = (
    nodeId: number,
    nodeUUID: string,
    parentNode: TemplateNodeSchema | null,
  ) => {
    if (parentNode) {
      const { copiedNode, flattenChildren } = copyNodeParams(
        templateNodes,
        nodeUUID,
      )
      if (copiedNode && flattenChildren) {
        copySectionToNode(nodeId, copiedNode, flattenChildren, parentNode.id)
      }
    } else {
      const { copiedNode, flattenChildren } = copyPageParams(
        templateNodes,
        nodeUUID,
      )
      if (copiedNode && flattenChildren) {
        copySectionToPage(nodeId, copiedNode, flattenChildren, activePageId)
      }
    }
  }

  const [isOpen, setIsOpen] = useState(true)

  const ref = useRef<HTMLDivElement>(null)
  const [hoverPosition, setHoverPosition] =
    useState<HoveringPosition>(undefined)

  const [{ isOver }, drop] = useDrop({
    accept: 'NODE',
    collect: (monitor: DropTargetMonitor) => ({
      isOver: monitor.isOver({ shallow: true }),
    }),
    hover: (_: DragItem, monitor: DropTargetMonitor) => {
      if (monitor.isOver({ shallow: true })) {
        const position = DnDUtils.getHoveringPosition(ref, monitor)
        if (hoverPosition !== position) {
          setHoverPosition(position)
        }
      } else {
        setHoverPosition(undefined)
      }
    },
    drop: (dragingItem: DragItem, monitor: DropTargetMonitor) => {
      const dragNode = dragingItem.node
      if (dragNode.id === props.node.id) {
        return
      }
      // 表（またはそれを含むセクション）を従業員チェック内に移動させない
      if (
        shouldBlockTableMoveIntoEmployeeCheck(
          dragNode,
          props.parentNode,
          templateNodes,
        )
      ) {
        showError(
          ERROR_MESSAGES.TABLE_MOVE_TO_EMPLOYEE_CHECK.title,
          ERROR_MESSAGES.TABLE_MOVE_TO_EMPLOYEE_CHECK.message,
        )
        return
      }
      const position = DnDUtils.getHoveringPosition(ref, monitor)
      const event: MoveNodeEvent = {
        dragNode: dragNode,
        dropNode: props.node,
        dropPosition: position,
        dragNodeParent: dragingItem.parentNode,
        dropNodeParent: props.parentNode,
      }
      moveNode(event)
    },
  })

  const [{ isDragging }, drag, preview] = useDrag({
    type: 'NODE',
    item: { node: props.node, parentNode: props.parentNode },
    collect: monitor => ({
      isDragging: monitor.isDragging(),
    }),
  })

  // eslint-disable-next-line react-hooks/refs -- レンダー中のref参照は既存設計のため、リファクタリング時に修正する
  drop(ref)

  if (isDragging) {
    return <></>
  }

  const isShownDragHandle = !props.node.section?.isEmployeeCheck
  const isTableSection = isTableSectionNode(props.node)
  const containsTable = containsTableSection(props.node, templateNodes)

  const showEmployeeCheck = () => {
    const firstPage = Object.values(templatePages)[0]
    if (!firstPage || firstPage.id !== activePageId) {
      return false
    }
    const sectionNodes = Object.values(templateNodes).filter(
      node => node.type === TemplateNodeTypeEnum.Section,
    )
    const employeeCheckNode = sectionNodes.find(
      node => node.section && node.section.isEmployeeCheck,
    )
    if (employeeCheckNode) {
      return employeeCheckNode.id === props.node.id
    }
    const activePage = templatePages[activePageId]
    const activePageNodes = activePage?.nodes ?? []
    let nodeId = 0
    for (const pageNodeId of activePageNodes) {
      if (sectionNodes.some(node => node.id === pageNodeId)) {
        nodeId = pageNodeId
        break
      }
    }
    return nodeId === props.node.id
  }

  return (
    <>
      <div key={props.node.id} data-testid="section">
        <AddTemplateModifyButtonGroup
          isOpen={props.isActive}
          node={props.node}
          position="top"
          parentNode={props.parentNode}
          addTemplateNode={addTemplateNode}
        />
        <div ref={ref} data-testid="section-bar">
          {isOver && hoverPosition === 'TOP' && <DraggingPlaceholder />}
          <Table onClick={() => changeActiveNodeId(props.node.id)}>
            <SectionTableCell>
              <SectionWrapper ref={preview}>
                <SectionOpenButtonWrapper>
                  <IconButton
                    size="small"
                    aria-label={
                      isOpen ? 'セクションを閉じる' : 'セクションを開く'
                    }
                    aria-expanded={isOpen}
                    onClick={() => setIsOpen(!isOpen)}
                    sx={{
                      bgcolor: colors.white,
                      color: colors.action.active,
                      '&:hover': {
                        bgcolor: colors.white,
                        opacity: 0.9,
                      },
                    }}
                  >
                    {isOpen ? <ExpandMore /> : <ChevronRight />}
                  </IconButton>
                </SectionOpenButtonWrapper>
                {isShownDragHandle && (
                  <DragHandle ref={drag}>
                    <HandleIcon />
                  </DragHandle>
                )}
                <SectionNameParagraphWrapper>
                  {props.isActive ? (
                    <SelectNodeEditor
                      onChangeText={(text: string) =>
                        changeSectionToNext(props.node.id, {
                          ...props.node,
                          section: { ...props.node.section, name: text },
                        })
                      }
                      onUpdate={(text: string) =>
                        updateTemplateNode(props.node.id, {
                          ...props.node,
                          section: { ...props.node.section, name: text },
                        })
                      }
                      name={props.node.section?.name ?? ''}
                      node={props.node}
                    />
                  ) : (
                    <Typography
                      variant="body2"
                      noWrap
                      sx={{
                        overflow: 'hidden',
                        width: '100%',
                        textOverflow: 'ellipsis',
                      }}
                      color="primary.contrastText"
                    >
                      {props.node.section?.name ?? ''}
                    </Typography>
                  )}
                </SectionNameParagraphWrapper>
                {/* 表セクションは行単位で繰り返しを設定するため、セクション単位の繰り返しは非対応 */}
                {!isTableSection && (
                  <FormControlLabel
                    sx={{ ml: 1 }}
                    control={
                      <Checkbox
                        disabled={props.node.section?.isEmployeeCheck === 1}
                        checked={props.node.section?.isRepeat === 1}
                        onClick={() => {
                          updateTemplateNode(props.node.id, {
                            ...props.node,
                            section: {
                              ...props.node.section,
                              isRepeat:
                                (props.node.section?.isRepeat ?? 0) === 0
                                  ? 1
                                  : 0,
                            },
                          })
                        }}
                        sx={{
                          color: colors.primary.main,
                          '&.Mui-checked': {
                            color: colors.primary.main,
                          },
                          '& .MuiSvgIcon-root': {
                            color: '#ffffff',
                          },
                        }}
                      />
                    }
                    label={
                      <CheckBoxText>このセクションを繰り返す</CheckBoxText>
                    }
                  />
                )}
                {/* FIXME: 複雑になるため、現時点では表は従業員チェックに非対応にしているが、将来的には対応する必要がある。 */}
                {!containsTable && showEmployeeCheck() && !isAudit && (
                  <FormControlLabel
                    sx={{ ml: 1 }}
                    control={
                      <Checkbox
                        checked={props.node.section?.isEmployeeCheck === 1}
                        // セクション直下の最初のノードとして、従業員タイプの質問ノードを追加または削除する
                        onClick={() => {
                          const isEmployeeCheck =
                            props.node.section?.isEmployeeCheck ?? 0
                          updateTemplateNode(props.node.id, {
                            ...props.node,
                            section: {
                              ...props.node.section,
                              isEmployeeCheck: isEmployeeCheck === 0 ? 1 : 0,
                              isRepeat:
                                isEmployeeCheck === 0
                                  ? 1
                                  : props.node.section?.isEmployeeCheck,
                            },
                          })
                          if (isEmployeeCheck === 0) {
                            // セクション直下の先頭に従業員タイプの質問ノードを追加または更新する
                            const question = {
                              responseType: ResponseTypeEnum.EMPLOYEE,
                              responseEmployees: [
                                {
                                  subType: ResponseEmployeeSubTypeEnum.NAME,
                                  isMultiple: 0,
                                },
                              ],
                              isRequired: 0,
                              isTimeDisplayed: 0,
                            }
                            const firstChildId = props.node.nodes[0]
                            const firstChild =
                              firstChildId !== undefined
                                ? templateNodes[firstChildId]
                                : undefined
                            if (
                              firstChild?.type ===
                                TemplateNodeTypeEnum.Question &&
                              firstChild.question?.responseType ===
                                ResponseTypeEnum.EMPLOYEE
                            ) {
                              // 先頭が既に従業員質問の場合は重複させず更新のみ
                              updateTemplateNode(firstChild.id, {
                                ...firstChild,
                                question: {
                                  ...firstChild.question,
                                  ...question,
                                },
                              })
                            } else if (firstChild) {
                              // 既存の先頭子ノードをアンカーにして、その上に挿入する
                              createNode(templateNodes, dispatch, {
                                targetNode: firstChild,
                                parentNode: props.node,
                                position: 'top',
                                addTemplateNodeType:
                                  TemplateNodeTypeEnum.Question,
                                question,
                              })
                            } else {
                              // 子ノードがない場合はそのまま追加
                              createNode(templateNodes, dispatch, {
                                targetNode: null,
                                parentNode: props.node,
                                position: 'bottom',
                                addTemplateNodeType:
                                  TemplateNodeTypeEnum.Question,
                                question,
                              })
                            }
                          } else {
                            // ここは従業員タイプの質問ノードを削除する場合のブロック
                            const nodeId = props.node.nodes[0]
                            if (nodeId === undefined) return
                            const deleteNode = templateNodes[nodeId]
                            if (
                              deleteNode?.type ===
                                TemplateNodeTypeEnum.Question &&
                              deleteNode.question?.responseType ===
                                ResponseTypeEnum.EMPLOYEE
                            ) {
                              deleteTemplateNode(deleteNode.id, props.node)
                            }
                          }
                        }}
                        sx={{
                          color: colors.primary.main,
                          '&.Mui-checked': {
                            color: colors.primary.main,
                          },
                          '& .MuiSvgIcon-root': {
                            color: '#ffffff',
                          },
                        }}
                      />
                    }
                    label={<CheckBoxText>従業員チェック</CheckBoxText>}
                  />
                )}
                {/* FIXME: 複雑になるため、現時点では表は従業員チェックに非対応にしているが、将来的には対応する必要がある。 */}
                {!containsTable && showEmployeeCheck() && isAudit && (
                  <Tooltip
                    placement="top"
                    title="監査機能が有効なため、使用できません"
                  >
                    <span>
                      <FormControlLabel
                        sx={{ ml: 1 }}
                        control={<Checkbox checked={false} disabled />}
                        label={<CheckBoxText>従業員チェック</CheckBoxText>}
                      />
                    </span>
                  </Tooltip>
                )}
                <Box>
                  {!props.node.section?.isEmployeeCheck && (
                    <Tooltip title="セクションをコピー">
                      <IconButton
                        onClick={() => {
                          if (props.node.uuid) {
                            copySection(
                              props.node.id,
                              props.node.uuid,
                              props.parentNode,
                            )
                          }
                        }}
                        aria-label="セクションをコピー"
                      >
                        <ContentCopyOutlined
                          sx={{ color: colors.primary.light }}
                        />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="セクションを削除">
                    <IconButton
                      onClick={() => {
                        const content: Array<ReactNode> = []
                        if (props.node.nodes?.length) {
                          content.push(
                            '紐づく質問やセクションも一緒に削除されます。',
                          )
                        }
                        const flowNames =
                          getRelatedExcelConversionFlowInSection(
                            props.node.id,
                            templateNodes,
                            excelConversionFlows,
                          )
                        if (flowNames.length > 0) {
                          content.push(
                            <>
                              <br />
                              <br />
                              以下のExcel変換設定に紐づいています。
                              <ConfirmModalContentList>
                                {flowNames.map(name => (
                                  <li key={name}>{name}</li>
                                ))}
                              </ConfirmModalContentList>
                            </>,
                          )
                        }
                        DeleteConfirmDialog({
                          title: 'セクションを削除してもよろしいですか？',
                          content,
                          onOk() {
                            deleteTemplateNode(props.node.id, props.parentNode)
                          },
                        })
                      }}
                      aria-label="セクションを削除"
                    >
                      <DeleteOutline sx={{ color: colors.error.light }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </SectionWrapper>
            </SectionTableCell>
            {isShowScore && (
              <BasicTableCell>
                <ColumnText>{sumSectionScore(props.node)}</ColumnText>
              </BasicTableCell>
            )}
          </Table>
          {!isOpen && isOver && hoverPosition === 'BOTTOM' && (
            <DraggingPlaceholder />
          )}
        </div>
        <SectionContentWrapper isOpened={isOpen} key={props.node?.id}>
          <SectionContent
            node={props.node}
            activeNodeId={activeNodeId}
            path={props.path}
            useTemplateNodeNodes={useTemplateNodeNodes}
            addTemplateNode={addTemplateNode}
            moveNode={moveNode}
          />
        </SectionContentWrapper>
        <AddTemplateModifyButtonGroup
          isOpen={props.isActive}
          node={props.node}
          position="bottom"
          parentNode={props.parentNode}
          addTemplateNode={addTemplateNode}
        />
      </div>
      {moveErrorDialog}
    </>
  )
}

export default Section
